/**
 * T-06 / T-1g-MULTIPART — the actual group-commit send logic implementing
 * `DispatchSender`: PUT every not-yet-delivered part (content-addressed,
 * idempotent), and only once ALL parts are durable, POST the one
 * commit-carrying Event for the group. Resume skips already-landed parts.
 *
 * One capture group ⇒ one Event ⇒ one `client_id` ⇒ one outbox record +
 * N media children (§2.1).
 */

import { extFromMime } from "./blob-store";
import { getBlobsForCapture, putOutboxRecord } from "./db";
import type { CaptureEnvelope, OutboxRecord, ReplyEnvelope } from "./types";
import type { DispatchSender } from "./dispatcher";

/** §2.2 the outbox-admission bound: a group with >20 parts is rejected at
 * save, not silently truncated at send time. */
export const MAX_PARTS = 20;

export function assertPartsWithinBound(count: number): void {
  if (count > MAX_PARTS) {
    throw new Error(`retry-unit: capture group exceeds the ${MAX_PARTS}-part bound (§2.2)`);
  }
}

/** The dev-store transport for the commit-carrying Event itself (the
 * `/mark/message` POST). Real transport is 1l's live server (gated); this
 * batch wires a mock/dev implementation via tests. */
export interface EventTransport {
  postCommitEvent(client_id: string, envelope: CaptureEnvelope, signal?: AbortSignal): Promise<ReplyEnvelope>;
}

/** Content-addressed blob PUT, injected so retry-unit doesn't hard-depend
 * on blob-store's module-level Map (keeps it swappable for the live 1l PUT
 * endpoint later without changing this file's control flow).
 *
 * `scope` (tenant_id + project_ref) is REQUIRED by the live HTTP putter so
 * the server writes to the goalkeeper-readable
 * `MARK_MEDIA_ROOT/{tenant}/{project}/media/...` tree (tenant_ctx.rs:532).
 * It is optional on the interface so the in-memory dev/test putter (which
 * ignores it) needs no signature change. */
export interface BlobPutScope {
  tenant_id: string;
  project_ref: string;
}
export interface BlobPutter {
  putBlob(
    capture_id: string,
    sha256: string,
    ext: string,
    mime: string,
    blob: Blob,
    scope?: BlobPutScope,
    signal?: AbortSignal,
  ): Promise<{ key: string }>;
}

export class RetryUnit implements DispatchSender {
  constructor(
    private transport: EventTransport,
    private blobPutter: BlobPutter,
    private ownerMatches: (signal?: AbortSignal) => Promise<boolean> = async () => true,
  ) {}

  async sendGroup(record: OutboxRecord, signal?: AbortSignal): Promise<ReplyEnvelope> {
    assertPartsWithinBound(record.envelope.media.length);

    const localParts = await getBlobsForCapture(record.capture_id);
    const byIndex = new Map(localParts.map((p) => [p.part_index, p]));

    for (const partRef of record.envelope.media) {
      if (record.parts_delivered[partRef.part_index]) continue; // resume: skip landed parts
      if (!(await this.ownerMatches(signal))) return { status: "owner-changed" };

      const stored = byIndex.get(partRef.part_index);
      if (!stored) {
        throw new Error(
          `retry-unit: missing local blob for capture_id=${record.capture_id} part_index=${partRef.part_index}`,
        );
      }

      const ext = extFromMime(partRef.mime);
      const result = await this.blobPutter.putBlob(
        record.capture_id,
        partRef.sha256,
        ext,
        partRef.mime,
        stored.blob,
        {
          tenant_id: record.envelope.tenant_id,
          project_ref: (record.envelope.payload["project_ref"] as string | undefined) ?? "",
        },
        signal,
      );
      if (!(await this.ownerMatches(signal))) return { status: "owner-changed" };
      partRef.media_ref = result.key;
      record.parts_delivered[partRef.part_index] = true;
    }

    const allDurable = record.envelope.media.every((p) => record.parts_delivered[p.part_index]);
    if (!allDurable) {
      // Unreachable given the loop above unless a PUT threw (which
      // propagates), but guards against a future refactor silently
      // releasing the commit event before every part landed.
      return { status: "transient-fail-retry" };
    }

    // Persist part-delivery progress + resolved media_refs durably before
    // the network attempt, so a crash mid-POST still resumes correctly.
    await putOutboxRecord(record);

    if (!(await this.ownerMatches(signal))) return { status: "owner-changed" };
    const reply = await this.transport.postCommitEvent(record.client_id, record.envelope, signal);
    return (await this.ownerMatches(signal)) ? reply : { status: "owner-changed" };
  }
}
